import i18next, { type Resource, createInstance, type i18n } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HTTPBackend from 'i18next-http-backend';
import resourcesToBackend from 'i18next-resources-to-backend';
import { initReactI18next } from 'react-i18next/initReactI18next';
import { z } from 'zod';
import { makeZodI18nMap } from 'zod-i18n-map';
import zodEN from 'zod-i18n-map/locales/en/zod.json';
import zodES from 'zod-i18n-map/locales/es/zod.json';
import i18nConfig from '@/i18nConfig';
import zodCustomEN from '@/zod-locales/en.json';
import zodCustomES from '@/zod-locales/es.json';

export const i18nInstance = createInstance();

export default async function initTranslations(
  locale: string,
  namespaces: string[] = ['common'],
  resources?: Resource,
): Promise<{ i18n: i18n; resources: Resource; t: i18n['t'] }> {
  i18nInstance.use(initReactI18next).use(LanguageDetector).use(HTTPBackend);

  if (!resources) {
    i18nInstance.use(
      resourcesToBackend(
        (language: string, namespace: string) =>
          import(`../../../public/locales/${language}/${namespace}.json`),
      ),
    );
  }

  await i18nInstance.init({
    lng: locale,
    resources: {
      es: {
        zod: zodES,
        zodCustom: zodCustomES,
      },
      en: {
        zod: zodEN,
        zodCustom: zodCustomEN,
      },
      ...resources,
    },
    fallbackLng: i18nConfig.defaultLocale,
    supportedLngs: i18nConfig.locales,
    defaultNS: i18nConfig.defaultNS,
    fallbackNS: i18nConfig.fallbackNS,
    partialBundledLanguages: true,
    ns: namespaces,
    preload: resources ? [] : i18nConfig.locales,
  });

  z.setErrorMap(makeZodI18nMap({ t: i18next.t, ns: ['zod', 'zodCustom'] }));

  return {
    i18n: i18nInstance,
    resources: i18nInstance.services.resourceStore.data,
    t: i18nInstance.t,
  };
}

export const isSupportedLanguage = (lang: string) => {
  return i18nConfig.locales.includes(lang);
};

export function i18nLocale(): { locale: string; t: i18n['t'] } {
  return {
    locale: i18nInstance.language,
    t: i18nInstance.t,
  };
}
